import axios from 'axios';
import {DEFAULT_LOCALE, DEFAULT_ORG_CODE, LOCAL_STORAGE_KEYS, LOCALES_ENABLED} from "./constants";

axios.defaults.headers.post['Content-Type'] = 'application/json';
const base_uri = process.env.REACT_APP_MYDQ_API;

const logError = (message) => {
    console.log('error_message from API --> ', message);
};

/**
 * Is this failure genuinely a connectivity problem?
 *
 * ORDER MATTERS. A response — any response, including 419 or 500 — proves the
 * connection works, so it is checked FIRST. Consulting navigator.onLine first
 * (as this did) turned every expired-token 419 into "No internet connection",
 * masking the real error and telling the user to check their internet: PBDQ-154.
 *
 * navigator.onLine only reports whether the OS has a network interface, not
 * whether anything is reachable, and it flips spuriously on VPN changes, Wi-Fi
 * handoff and sleep/wake. It is now the LAST resort, used only when there is no
 * response to contradict it.
 */
function isNetworkFailure(error) {
    // The server answered — definitively not a connectivity problem.
    if (error && error.response) return false;

    const msg = String((error && error.message) || '').toLowerCase();
    if (msg.includes('network') || (error && error.code === 'ERR_NETWORK')) return true;

    // No response and no network-shaped error: fall back to the browser's hint.
    return typeof navigator !== 'undefined' && !navigator.onLine;
}

export async function invokeApi(
    {
        path,
        method = 'GET',
        headers = {},
        queryParams = {},
        postData = {},
    }
) {

    const org_code = localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVE_ORG_CODE)

    // Get locale from URL first (to avoid race condition when URL changes)
    // Falls back to localStorage if URL doesn't have a valid locale
    const currentPath = window.location.pathname;
    const localeFromUrl = currentPath.split('/')[1];
    const locale = (localeFromUrl && localeFromUrl.length === 2 && LOCALES_ENABLED.includes(localeFromUrl))
        ? localeFromUrl
        : localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVE_LOCALE);
    if (org_code) {
        headers = {
            ...headers,
            'xc-org-code': org_code,
            'xc-dq-locale': locale || DEFAULT_LOCALE
        }
    } else {
        const active_user = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_USER));
        if (active_user && active_user.is_guest_user) {
            headers = {
                ...headers,
                'xc-org-code': DEFAULT_ORG_CODE,
                'xc-dq-locale': locale || DEFAULT_LOCALE
            }
        }
    }

    let reqObj = {
        method,
        url     : base_uri + path,
        headers
    };

    reqObj['params'] = queryParams;

    if (method === 'POST') {
        reqObj['data'] = postData;
    }

    let results;

    try {
        // fall back option if org still not in request
        if (!headers['xc-org-code']) {
            const org_code_store = localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVE_ORG_CODE);
            if (org_code_store) {
                headers['xc-org-code'] = org_code_store;
            }
        }

        results = await axios(reqObj);

        /**
         * A completed round trip is proof the connection works.
         *
         * The offline banner is driven by `navigator.onLine`, which the browser
         * sometimes reports incorrectly — that is how PBDQ-154 was captured with
         * "You're offline" on screen while the very same page was receiving HTTP
         * 419 responses from the API. Announcing a real success lets the banner
         * correct itself instead of sitting there contradicting the network tab.
         */
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('api-reachable'));
        }
        return results.data;
    } catch (error) {
        if (isNetworkFailure(error)) {
            throw new Error('NETWORK ERROR : No internet connection');
        }

        const status = error.response?.status;

        if (status === 499) throw new Error('Permission denied');

        if (status === 401) {
            localStorage.clear();
            window.location.reload();
        }

        if (!(error.response && error.response.data && error.response.data.message)) {
            logError('NETWORK ERROR : Some Thing Went Wrong!');
            throw new Error('NETWORK ERROR : Some Thing Went Wrong!');

        } else {
            logError(error.response.data.message);
            const apiError = new Error(error.response.data.message);
            // What the API sent along with the message, for a caller that needs it.
            apiError.data = error.response.data.data;
            throw apiError;
        }
    }
}
